/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentItemData } from '../../component-classes/data/component-item-data.class';
import { deepCopy } from '../../helpers';
import { getFuelSkuSuffix } from '../../helpers/fuel-choice';

/**
 * Schema update from version 19 to version 20
 * - Re-points every placed fuel appliance from its pre-split base catalog row to the LP or NG
 *   row matching its own kitchen's fuelchoice. Fuel used to be a suffix appended to the sku on
 *   read; it is now a real catalog row per fuel, each carrying its own HubSpot product id. Each
 *   design is migrated against the fuel it was actually drawn with, so nothing collapses onto
 *   one variant. An item pinned to its own fuelchoice migrates to that fuel instead.
 * - A no-op when the catalog is not loaded, or is not yet split into variants; Item resolves the
 *   family at construction either way, so an unmigrated design still renders the right row.
 */

type VariantLookup = {
	byId: Map<string, any>;
	byFamilyFuel: Map<string, any>;
};

const buildVariantLookup = (): VariantLookup => {
	const byId = new Map<string, any>();
	const byFamilyFuel = new Map<string, any>();

	Object.values(ComponentItemData.getData() ?? {}).forEach((component: any) => {
		if (component?.id) byId.set(component.id, component);
		if (component?.fuelFamily && component?.fuelType) byFamilyFuel.set(`${component.fuelFamily}:${component.fuelType}`, component);
	});

	return { byId, byFamilyFuel };
};

const repointIsland = (island: any, kitchenFuel: string, lookup: VariantLookup): void => {
	island?.panels?.children?.forEach((panel: any) => {
		panel?.panelItemGroups?.children?.forEach((group: any) => {
			group?.items?.children?.forEach((panelItem: any) => {
				const item = panelItem?.item;
				if (!item?.id || item.override) return;

				const current = lookup.byId.get(item.id);
				if (!current?.fuelFamily) return;

				const variant = lookup.byFamilyFuel.get(`${current.fuelFamily}:${getFuelSkuSuffix(item.fuelchoice ?? kitchenFuel)}`);
				if (variant?.id && variant.id !== item.id) item.id = variant.id;
			});
		});
	});
};

export const u20 = (blueprint: any): any => {
	console.log('schema-update:process-update:20:started');

	try {
		const updatedBlueprint = deepCopy(blueprint);
		updatedBlueprint.schemaVersion = 20;

		const lookup = buildVariantLookup();
		if (lookup.byFamilyFuel.size === 0) {
			console.log('schema-update:process-update:20:skipped - catalog has no fuel variants loaded');
			return updatedBlueprint;
		}

		updatedBlueprint.kitchens?.children?.forEach((kitchen: any) => {
			const kitchenFuel = kitchen?.fuelchoice ?? null;
			[...(kitchen?.design?.islands?.children ?? []), ...(kitchen?.design?.obstructions?.children ?? [])]
				.forEach((island: any) => repointIsland(island, kitchenFuel, lookup));
		});

		console.log('schema-update:process-update:20:completed');
		return updatedBlueprint;
	} catch (err) {
		console.error('schema-update:process-update:20:error', err);
		return null;
	}
};
